/* =============================================================================
   WHEELWORK CUSTOMS — THEME V3 · global.js
   Small, dependency-free modules that run on every page. Heavier tools
   (fitment finder, configurator, gallery) load their own scripts.
============================================================================= */
(function () {
  'use strict';
  var W = window.WWC = window.WWC || {};
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- helpers ---------- */
  W.$ = function (s, r) { return (r || document).querySelector(s); };
  W.$$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };
  W.esc = function (s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); };
  W.money = function (cents) {
    var fmt = W.moneyFormat || '${{amount}}';
    var v = (Number(cents) / 100);
    var withDec = v.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    var noDec = Math.round(v).toLocaleString('en-AU');
    return fmt.replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDec).replace(/\{\{\s*amount\s*\}\}/, withDec).replace(/<[^>]*>/g, '');
  };
  W.debounce = function (fn, ms) { var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); }; };
  W.store = {
    get: function (k) { try { return JSON.parse(localStorage.getItem(k)); } catch (e) { return null; } },
    set: function (k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } }
  };

  /* ---------- fitment database (shared, fetched once, on demand) ---------- */
  var dbPromise = null;
  W.loadFitment = function () {
    if (!dbPromise) {
      dbPromise = fetch(W.fitmentDb, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('Fitment data failed to load'); return r.json(); })
        .catch(function (e) { dbPromise = null; throw e; });
    }
    return dbPromise;
  };
  W.fitmentHref = function (make, model, year) {
    var q = new URLSearchParams();
    if (make) q.set('make', make);
    if (model) q.set('model', model);
    if (year) q.set('year', year);
    return (W.fitmentUrl || '/pages/fitment-tool') + '?' + q.toString() + '#fitment';
  };

  /* ---------- fitment maths (shared by the fitment tool and the flow-formed pickers) ---------- */
  var DEFAULT_LIMITS = [12, 8, 25, 12]; // mm vs factory: street out, street in, max out, max in
  W.fit = {
    normPCD: function (s) { return String(s).replace(/[×xX\s]/g, 'x').replace(/\s/g, '').toLowerCase(); },
    midRange: function (str) {
      var m = String(str).match(/(-?\d+(?:\.\d+)?)\s*[–\-]\s*(-?\d+(?:\.\d+)?)/);
      if (m) return (parseFloat(m[1]) + parseFloat(m[2])) / 2;
      var s = String(str).match(/(-?\d+(?:\.\d+)?)/);
      return s ? parseFloat(s[1]) : null;
    },
    // Reads a variant's size / bolt pattern / offset. Uses the product's option
    // names when available ("Wheel Size", "Bolt Pattern", "Offset"), else parses
    // the title. Handles "19 x 9.5 in / 5 x 120 / 35 mm", "19x9.5 / 5x120 / ET35",
    // "17 x 7.5 in / 38 mm / 5 x 114.3" etc. Variants can carry option1–3
    // (products.json) or an options array (the product page's variant data).
    parseVariant: function (v, p) {
      var out = { pcd: null, dia: null, width: null, et: null };
      var parts = [];
      var vals = v && v.options ? v.options : v && v.option1 !== undefined ? [v.option1, v.option2, v.option3] : null;
      if (p && p.options && vals) {
        p.options.forEach(function (o, i) { parts.push({ name: String(o.name || o).toLowerCase(), val: vals[i] }); });
      } else {
        String(v && v.title !== undefined ? v.title : v).split('/').forEach(function (x) { parts.push({ name: '', val: x }); });
      }
      parts.forEach(function (pt) {
        var s = String(pt.val == null ? '' : pt.val).toLowerCase().replace(/\s+/g, '').replace(/[×*]/g, 'x');
        var mSize = s.match(/^(\d{2}(?:\.\d+)?)(?:in|inch|")?x(\d{1,2}(?:\.\d+)?)(?:in|inch|j|")?$/);
        var mPcd = s.match(/^(\d)x(\d{2,3}(?:\.\d+)?)(?:mm)?$/);
        var mEt = s.match(/^(?:et|\+)?(-?\d{1,3})(?:mm)?$/);
        var mDia = s.match(/^(\d{2})(?:in|inch|")$/);
        // compact "1565" = 15x6.5, "1880" = 18x8.0, "18105" = 18x10.5
        var mCompact = (!pt.name || /size/.test(pt.name)) && s.match(/^(1[3-9]|2[0-6])(\d)(\d{1,2})$/);
        if (mSize && +mSize[1] >= 13 && +mSize[1] <= 26) { out.dia = parseFloat(mSize[1]); out.width = parseFloat(mSize[2]); }
        else if (mPcd) { out.pcd = mPcd[1] + 'x' + mPcd[2]; }
        else if (mCompact && s.length >= 4) {
          out.dia = parseFloat(mCompact[1]);
          out.width = mCompact[3].length === 2 ? parseFloat(mCompact[2] + mCompact[3][0] + '.' + mCompact[3][1]) : parseFloat(mCompact[2] + '.' + mCompact[3]);
        }
        else if (mDia) { out.dia = parseFloat(mDia[1]); }
        else if (mEt && (!pt.name || /offset|et/.test(pt.name))) { out.et = parseInt(mEt[1], 10); }
      });
      return out;
    },
    target: function (d, style) {
      if (style === 'oem') return { w: d.stockF.w, et: d.stockF.et, dia: d.stockF.d };
      var src = style === 'agg' ? d.agg : d.street;
      return { w: W.fit.midRange(src.w) || d.stockF.w, et: W.fit.midRange(src.et) || d.stockF.et, dia: W.fit.midRange(src.d) || d.stockF.d };
    },
    // Where a wheel sits against factory, rim edges only, worse axle wins (mm):
    // out = further out toward the guard, in = closer to the strut. level 0 street, 1 tight, 2 won't fit.
    place: function (d, w, et, classLimits) {
      var res = null;
      [['F', d.stockF, d.lim], ['R', d.stockR || d.stockF, d.limR || d.lim]].forEach(function (a) {
        var s = a[1], L = a[2] || classLimits || DEFAULT_LIMITS;
        var dOut = (w - s.w) * 12.7 - (et - s.et), dIn = (w - s.w) * 12.7 + (et - s.et);
        var level = dOut > L[2] || dIn > L[3] ? 2 : dOut > L[0] || dIn > L[1] ? 1 : 0;
        if (!res || level > res.level) res = { dOut: Math.round(dOut), dIn: Math.round(dIn), level: level };
      });
      return res;
    },
    // Best variant for a car: bolt pattern must match, then the offset that sits within
    // street limits and closest to the street target; smaller than factory diameter is a brake risk.
    bestFlow: function (variants, p, d, classLimits) {
      var pcd = W.fit.normPCD(d.bolt), tgt = W.fit.target(d, 'street'), best = null;
      variants.forEach(function (v) {
        if (v.available === false) return;
        var sp = W.fit.parseVariant(v, p);
        if (!sp.pcd || W.fit.normPCD(sp.pcd) !== pcd) return;
        var pl = sp.width != null && sp.et != null ? W.fit.place(d, sp.width, sp.et, classLimits) : { level: 1 };
        var cost = pl.level * 1000;
        cost += sp.et != null ? Math.abs(sp.et - tgt.et) * 1.2 : 5;
        cost += sp.width != null ? Math.abs(sp.width - tgt.w) * 8 : 6;
        cost += sp.dia != null ? Math.abs(sp.dia - tgt.dia) * 5 + (sp.dia < d.stockF.d ? 200 : 0) : 6;
        if (!best || cost < best.cost) best = { cost: cost, variant: v, sp: sp, place: pl, smaller: sp.dia != null && sp.dia < d.stockF.d };
      });
      return best;
    }
  };

  /* ---------- toast ---------- */
  var toastTimer;
  W.toast = function (html) {
    var t = W.$('[data-toast]');
    if (!t) return;
    t.innerHTML = html;
    t.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { t.classList.remove('is-visible'); }, 5000);
  };

  /* ---------- cart ---------- */
  W.updateCartCount = function (count) {
    W.$$('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = !count;
    });
    W.$$('[data-cart-label]').forEach(function (el) { el.setAttribute('aria-label', 'Cart, ' + count + (count === 1 ? ' item' : ' items')); });
  };
  W.addToCart = function (items) {
    return fetch(W.routes.cartAdd + '.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      return r.json().then(function (d) { if (!r.ok) throw new Error(d.description || d.message || 'Could not add to cart'); return d; });
    }).then(function (d) {
      return fetch(W.routes.cart + '.js').then(function (r) { return r.json(); }).then(function (cart) {
        W.updateCartCount(cart.item_count);
        W.toast('<svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg><span>Added to your cart.</span> <a href="' + W.routes.cart + '">View cart</a>');
        document.dispatchEvent(new CustomEvent('wwc:cart-updated', { detail: cart }));
        return d;
      });
    });
  };

  /* AJAX product forms: progressive enhancement over a normal POST.
     Only forms marked data-ajax-cart are intercepted, and only when the merchant
     enabled it — option apps that need a full POST keep working either way. */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-ajax-cart="true"]');
    if (!form || !window.fetch || !window.FormData) return;
    if (e.submitter && e.submitter.name === 'return_to') return; // "Buy now" goes straight to checkout
    e.preventDefault();
    var btn = form.querySelector('[type="submit"][name="add"]') || form.querySelector('[type="submit"]');
    var err = form.querySelector('[data-form-error]');
    if (btn) { btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true'); }
    if (err) { err.hidden = true; err.textContent = ''; }
    fetch(W.routes.cartAdd + '.js', { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: new FormData(form) })
      .then(function (r) { return r.json().then(function (d) { if (!r.ok) throw new Error(d.description || d.message || 'Could not add to cart'); return d; }); })
      .then(function () { return fetch(W.routes.cart + '.js').then(function (r) { return r.json(); }); })
      .then(function (cart) {
        W.updateCartCount(cart.item_count);
        if (form.dataset.redirect === 'cart') { window.location.href = W.routes.cart; return; }
        W.toast('<svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg><span>Added to your cart.</span> <a href="' + W.routes.cart + '">View cart &amp; checkout</a>');
      })
      .catch(function (ex) { if (err) { err.textContent = ex.message; err.hidden = false; } else { W.toast(W.esc(ex.message)); } })
      .finally(function () { if (btn) { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); } });
  });

  /* ---------- header: scrolled state ---------- */
  var header = W.$('[data-header]');
  if (header) {
    var onScroll = function () { header.classList.toggle('is-scrolled', window.scrollY > 8); };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    // Keep --header-h accurate for sticky offsets.
    var setH = function () { document.documentElement.style.setProperty('--header-h', header.offsetHeight + 'px'); };
    setH(); window.addEventListener('resize', W.debounce(setH, 150));
  }

  /* Desktop dropdowns: close others / on outside click / on Escape */
  W.$$('[data-dropdown]').forEach(function (d) {
    d.addEventListener('toggle', function () {
      if (d.open) W.$$('[data-dropdown]').forEach(function (o) { if (o !== d) o.open = false; });
    });
  });
  document.addEventListener('click', function (e) {
    W.$$('[data-dropdown][open]').forEach(function (d) { if (!d.contains(e.target)) d.open = false; });
  });

  /* ---------- modal-ish panels (drawer + search) with focus management ---------- */
  var lastFocus = null;
  function focusables(root) { return W.$$('a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])', root).filter(function (el) { return el.offsetParent !== null; }); }
  function openPanel(panel) {
    lastFocus = document.activeElement;
    panel.hidden = false;
    requestAnimationFrame(function () { panel.classList.add('is-open'); });
    document.body.classList.add('is-locked');
    W.$$('[aria-controls="' + panel.id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    var f = panel.querySelector('[data-autofocus]') || focusables(panel)[0];
    if (f) setTimeout(function () { f.focus(); }, 60);
  }
  function closePanel(panel) {
    panel.classList.remove('is-open');
    document.body.classList.remove('is-locked');
    W.$$('[aria-controls="' + panel.id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    setTimeout(function () { if (!panel.classList.contains('is-open')) panel.hidden = true; }, reduceMotion ? 0 : 260);
    if (lastFocus) lastFocus.focus();
  }
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-open-panel]');
    if (opener) { e.preventDefault(); var p = document.getElementById(opener.getAttribute('aria-controls')); if (p) openPanel(p); return; }
    var closer = e.target.closest('[data-close-panel]');
    if (closer) { e.preventDefault(); var panel = closer.closest('[data-panel]'); if (panel) closePanel(panel); }
  });
  document.addEventListener('keydown', function (e) {
    var open = W.$('[data-panel].is-open');
    if (!open) return;
    if (e.key === 'Escape') { closePanel(open); return; }
    if (e.key === 'Tab') {
      var f = focusables(open); if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---------- predictive search ---------- */
  var sInput = W.$('[data-predictive-input]');
  var sOut = W.$('[data-predictive-results]');
  if (sInput && sOut) {
    var ctrl;
    sInput.addEventListener('input', W.debounce(function () {
      var q = sInput.value.trim();
      if (q.length < 2) { sOut.innerHTML = ''; return; }
      if (ctrl) ctrl.abort();
      ctrl = window.AbortController ? new AbortController() : null;
      fetch(W.routes.search + '.json?q=' + encodeURIComponent(q) + '&resources[type]=product,collection,page&resources[limit]=6', ctrl ? { signal: ctrl.signal } : {})
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var res = d.resources && d.resources.results || {};
          var html = '';
          if (res.products && res.products.length) {
            html += '<p class="predictive__title">Wheels &amp; products</p>';
            res.products.forEach(function (p) {
              html += '<a href="' + p.url + '">' + (p.image ? '<img src="' + p.image + (p.image.indexOf('?') > -1 ? '&' : '?') + 'width=120" alt="" width="56" height="56" loading="lazy">' : '') + '<span><strong>' + W.esc(p.title) + '</strong><br><span class="muted small">From ' + W.esc(p.price) + '</span></span></a>';
            });
          }
          ['collections', 'pages'].forEach(function (k) {
            if (res[k] && res[k].length) {
              html += '<p class="predictive__title">' + (k === 'pages' ? 'Pages' : 'Collections') + '</p>';
              res[k].forEach(function (c) { html += '<a href="' + c.url + '"><span>' + W.esc(c.title) + '</span></a>'; });
            }
          });
          sOut.innerHTML = html || '<p class="muted small" style="padding:8px">No results for “' + W.esc(q) + '”. Press Enter to search everything.</p>';
        }).catch(function () { /* aborted or offline */ });
    }, 220));
  }

  /* ---------- announcement rotator ---------- */
  W.$$('[data-announcement]').forEach(function (bar) {
    // The first message is rendered with .is-active; this only rotates.
    var msgs = W.$$('.announcement__msg', bar);
    if (msgs.length < 2) return;
    var i = 0, paused = false;
    bar.addEventListener('mouseenter', function () { paused = true; });
    bar.addEventListener('mouseleave', function () { paused = false; });
    bar.addEventListener('focusin', function () { paused = true; });
    bar.addEventListener('focusout', function () { paused = false; });
    if (!reduceMotion) setInterval(function () {
      if (paused || window.innerWidth >= 1200 && bar.dataset.inline === 'true') return;
      msgs[i].classList.remove('is-active'); i = (i + 1) % msgs.length; msgs[i].classList.add('is-active');
    }, 4500);
  });

  /* ---------- hero slideshow ---------- */
  W.$$('[data-hero-slider]').forEach(function (hero) {
    var slides = W.$$('.hero__slide', hero), dots = W.$$('[data-hero-dot]', hero);
    if (slides.length < 2) return;
    var root = document.documentElement;
    var interval = (parseFloat(hero.dataset.interval) || 6) * 1000;
    var auto = hero.dataset.autoplay === 'true' && !reduceMotion && root.dataset.motion !== 'none';
    var i = 0, timer = null, start = 0, remaining = interval, userPaused = false, held = false;
    var pauseBtn = hero.querySelector('[data-hero-pause]');
    if (pauseBtn && !auto) pauseBtn.hidden = true;

    function playing() { return auto && !userPaused; }
    function preload(n) { W.$$('img', slides[n % slides.length]).forEach(function (img) { img.loading = 'eager'; }); }
    function restartProgress() {
      hero.classList.remove('is-playing');
      void hero.offsetWidth; // restart the CSS progress animation
      if (playing()) hero.classList.add('is-playing');
    }
    function schedule() {
      clearTimeout(timer);
      if (!playing() || held || document.hidden || root.classList.contains('is-loading')) return;
      start = Date.now();
      timer = setTimeout(function () { go(i + 1); }, remaining);
    }
    function go(n) {
      n = (n + slides.length) % slides.length;
      if (n === i) return;
      slides[i].classList.remove('is-active'); slides[i].setAttribute('aria-hidden', 'true'); slides[i].inert = true;
      if (dots[i]) { dots[i].classList.remove('is-active'); dots[i].removeAttribute('aria-current'); }
      i = n;
      slides[i].classList.add('is-active'); slides[i].removeAttribute('aria-hidden'); slides[i].inert = false;
      if (dots[i]) { dots[i].classList.add('is-active'); dots[i].setAttribute('aria-current', 'true'); }
      preload(i + 1);
      remaining = interval;
      restartProgress();
      schedule();
    }
    function hold() {
      if (held || !playing()) return;
      held = true; clearTimeout(timer);
      remaining = Math.max(400, remaining - (Date.now() - start));
      hero.classList.add('is-paused');
    }
    function release() {
      if (!held) return;
      held = false; hero.classList.remove('is-paused');
      schedule();
    }

    hero.querySelector('[data-hero-next]').addEventListener('click', function () { go(i + 1); });
    hero.querySelector('[data-hero-prev]').addEventListener('click', function () { go(i - 1); });
    dots.forEach(function (d) { d.addEventListener('click', function () { go(+d.dataset.heroDot); }); });
    if (pauseBtn) pauseBtn.addEventListener('click', function () {
      userPaused = !userPaused;
      pauseBtn.setAttribute('aria-pressed', userPaused);
      pauseBtn.setAttribute('aria-label', userPaused ? 'Play slideshow' : 'Pause slideshow');
      pauseBtn.querySelector('[data-icon-pause]').hidden = userPaused;
      pauseBtn.querySelector('[data-icon-play]').hidden = !userPaused;
      clearTimeout(timer); held = false; hero.classList.remove('is-paused');
      remaining = interval; restartProgress(); schedule();
    });
    hero.addEventListener('mouseenter', hold);
    hero.addEventListener('mouseleave', release);
    hero.addEventListener('focusin', hold);
    hero.addEventListener('focusout', function (e) { if (!hero.contains(e.relatedTarget)) release(); });
    document.addEventListener('visibilitychange', function () { if (document.hidden) hold(); else release(); });
    hero.addEventListener('keydown', function (e) {
      if (e.target.matches('input, textarea, select')) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); go(i + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(i - 1); }
    });
    // Swipe on touch screens (vertical scrolling still works)
    var sx = null, sy = null;
    hero.addEventListener('pointerdown', function (e) { if (e.pointerType !== 'mouse') { sx = e.clientX; sy = e.clientY; } }, { passive: true });
    hero.addEventListener('pointerup', function (e) {
      if (sx === null) return;
      var dx = e.clientX - sx, dy = e.clientY - sy; sx = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) go(i + (dx < 0 ? 1 : -1));
    }, { passive: true });

    preload(1);
    restartProgress();
    // Start once the intro loader (if any) has lifted
    if (root.classList.contains('is-loading') && window.MutationObserver) {
      var mo = new MutationObserver(function () { if (!root.classList.contains('is-loading')) { mo.disconnect(); restartProgress(); schedule(); } });
      mo.observe(root, { attributes: true, attributeFilter: ['class'] });
    } else schedule();
  });

  /* ---------- expressive motion: tag headings, cascade grids, drift photos ---------- */
  if (document.documentElement.dataset.motion === 'expressive' && !reduceMotion) {
    var skip = '.site-header, .site-footer, .hero, .pdp, dialog, .drawer, [data-no-reveal]';
    W.$$('.section-head h2, .section-head .h2, .page-hero h1, .qb__head h2, .fin-group__head h2, .fl-cta__title').forEach(function (h) {
      if (!h.closest(skip) && !h.closest('.reveal')) h.classList.add('reveal-heading');
    });
    W.$$('.section-head .lead, .section-head .eyebrow, .page-hero .lead').forEach(function (el) {
      if (!el.closest(skip) && !el.closest('.reveal')) { el.classList.add('reveal'); el.style.setProperty('--i', 1); }
    });
    W.$$('.product-grid > li, .vt > li, .gal > li, .fl > li, .fin-grid > li, .qb__card, .fl-bar, .trust-strip li, .faq details').forEach(function (el) {
      if (el.closest(skip) || el.classList.contains('reveal') || el.querySelector('.reveal')) return;
      var i = Array.prototype.indexOf.call(el.parentNode.children, el);
      el.classList.add('reveal');
      el.style.setProperty('--i', Math.min(i % 8, 7));
    });
    var plx = W.$$('.hero__media picture, .cta-banner__img img, .split .media > img').filter(function (el) { return !el.closest('[data-no-reveal]'); });
    if (plx.length) {
      plx.forEach(function (el) { el.setAttribute('data-parallax', ''); });
      var ticking = false, vh = window.innerHeight;
      var drift = function () {
        ticking = false;
        plx.forEach(function (el) {
          var box = el.parentNode.getBoundingClientRect();
          if (box.bottom < -100 || box.top > vh + 100) return;
          // drift a little slower than the page: up to ±8% of the image height
          var p = (box.top + box.height / 2 - vh / 2) / (vh + box.height);
          el.style.setProperty('--plx', (-p * box.height * 0.16).toFixed(1) + 'px');
        });
      };
      window.addEventListener('scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(drift); } }, { passive: true });
      window.addEventListener('resize', function () { vh = window.innerHeight; drift(); }, { passive: true });
      drift();
    }
  }

  /* ---------- phones: long lists show the first few, page intros show three lines ---------- */
  if (window.matchMedia('(max-width: 699px)').matches) {
    [['.facts dl', 3, 'Show all facts'], ['.accordion', 5, 'Show all questions']].forEach(function (c) {
      W.$$(c[0]).forEach(function (list) {
        var items = Array.prototype.slice.call(list.children).slice(c[1]);
        if (items.length < 2) return;
        items.forEach(function (el) { el.classList.add('is-more-hidden'); });
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn btn--secondary btn--sm more-btn';
        btn.textContent = c[2] + ' (' + list.children.length + ')';
        var showAll = function () { items.forEach(function (el) { el.classList.remove('is-more-hidden'); }); btn.remove(); };
        btn.addEventListener('click', showAll);
        list.insertAdjacentElement('afterend', btn);
        var search = list.parentNode.querySelector('[data-faq-search]');
        if (search) search.addEventListener('input', showAll, { once: true });
      });
    });
    W.$$('.page-hero .lead').forEach(function (el) {
      el.setAttribute('data-clamp', '');
      if (el.scrollHeight <= el.clientHeight + 4) { el.removeAttribute('data-clamp'); return; }
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'clamp-btn';
      b.textContent = 'Read more';
      b.setAttribute('aria-expanded', 'false');
      b.addEventListener('click', function () {
        var open = el.classList.toggle('is-open');
        b.textContent = open ? 'Show less' : 'Read more';
        b.setAttribute('aria-expanded', String(open));
      });
      el.insertAdjacentElement('afterend', b);
    });
  }

  /* ---------- reveal on scroll ---------- */
  if ('IntersectionObserver' in window && !reduceMotion && document.documentElement.dataset.motion !== 'none') {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        (en.target._reveal || [en.target]).forEach(function (t) { t.classList.add('is-in'); });
        io.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .05 });
    W.$$('.reveal').forEach(function (el) { io.observe(el); });
    // A heading starts fully clipped, which hides it from the observer, so watch its parent instead
    W.$$('.reveal-heading').forEach(function (h) {
      var p = h.parentNode;
      if (!p._reveal) p._reveal = p.classList.contains('reveal') ? [p] : [];
      p._reveal.push(h);
      io.observe(p);
    });
  } else {
    W.$$('.reveal, .reveal-heading').forEach(function (el) { el.classList.add('is-in'); });
  }

  /* ---------- footer accordions: open on desktop ---------- */
  var mqDesk = window.matchMedia('(min-width: 900px)');
  function syncFooter() { W.$$('.footer__col details').forEach(function (d) { d.open = mqDesk.matches; }); }
  syncFooter(); mqDesk.addEventListener ? mqDesk.addEventListener('change', syncFooter) : mqDesk.addListener(syncFooter);

  /* ---------- before / after sliders ---------- */
  W.$$('[data-ba]').forEach(function (ba) {
    var r = ba.querySelector('input[type="range"]');
    if (!r) return;
    var set = function () { ba.style.setProperty('--pos', r.value + '%'); };
    r.addEventListener('input', set); set();
  });

  /* ---------- click-to-play video (no autoplay download on mobile data) ---------- */
  W.$$('[data-video-play]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var wrap = btn.closest('[data-video]');
      var tpl = wrap.querySelector('template');
      if (tpl) { wrap.appendChild(tpl.content.cloneNode(true)); }
      var v = wrap.querySelector('video');
      btn.remove();
      var poster = wrap.querySelector('[data-video-poster]'); if (poster) poster.remove();
      if (v) { v.play().catch(function () {}); v.focus(); }
    });
  });

  /* ---------- FAQ filter ---------- */
  W.$$('[data-faq-search]').forEach(function (input) {
    var list = document.getElementById(input.getAttribute('aria-controls'));
    var empty = list && list.parentNode.querySelector('[data-faq-empty]');
    input.addEventListener('input', function () {
      var q = input.value.trim().toLowerCase(), shown = 0;
      W.$$('details', list).forEach(function (d) {
        var hit = !q || d.textContent.toLowerCase().indexOf(q) > -1;
        d.hidden = !hit; if (hit) shown++;
        if (q && hit) d.open = true; if (!q) d.open = false;
      });
      if (empty) empty.hidden = shown > 0;
    });
  });

  /* ---------- copy buttons ---------- */
  document.addEventListener('click', function (e) {
    var b = e.target.closest('[data-copy]');
    if (!b) return;
    var text = b.getAttribute('data-copy');
    (navigator.clipboard ? navigator.clipboard.writeText(text) : Promise.reject()).then(function () {
      var o = b.innerHTML; b.textContent = 'Copied'; setTimeout(function () { b.innerHTML = o; }, 1600);
    }).catch(function () { window.prompt('Copy this:', text); });
  });

  /* ---------- vehicle bar (header quick picker) ---------- */
  var GARAGE_KEY = 'wwc:garage';
  W.getGarage = function () { return W.store.get(GARAGE_KEY); };
  W.setGarage = function (v) { W.store.set(GARAGE_KEY, v); document.dispatchEvent(new CustomEvent('wwc:garage', { detail: v })); };

  W.vehicleSelects = function (root, opts) {
    opts = opts || {};
    var mk = root.querySelector('[data-vb="make"]'), md = root.querySelector('[data-vb="model"]'), yr = root.querySelector('[data-vb="year"]');
    if (!mk || !md || !yr) return null;
    var DB = null;
    function fill(sel, items, placeholder) {
      sel.innerHTML = '<option value="">' + placeholder + '</option>' + items.map(function (x) { return '<option value="' + W.esc(x) + '">' + W.esc(x) + '</option>'; }).join('');
      sel.disabled = !items.length;
    }
    function ensure() {
      if (DB) return Promise.resolve(DB);
      mk.setAttribute('aria-busy', 'true');
      return W.loadFitment().then(function (d) {
        DB = d.DB; mk.removeAttribute('aria-busy');
        var cur = mk.value;
        fill(mk, Object.keys(DB).sort(), 'Make');
        if (cur) mk.value = cur;
        return DB;
      }).catch(function () { mk.removeAttribute('aria-busy'); mk.innerHTML = '<option value="">Couldn’t load — tap to retry</option>'; });
    }
    ['pointerdown', 'focus', 'keydown'].forEach(function (ev) { mk.addEventListener(ev, ensure, { once: false, passive: true }); });
    mk.addEventListener('change', function () {
      fill(md, mk.value && DB[mk.value] ? Object.keys(DB[mk.value]) : [], 'Model');
      fill(yr, [], 'Year'); if (opts.onChange) opts.onChange(get());
    });
    md.addEventListener('change', function () {
      var years = mk.value && md.value ? Object.keys(DB[mk.value][md.value]) : [];
      fill(yr, years, 'Year');
      if (years.length === 1) yr.value = years[0];
      if (opts.onChange) opts.onChange(get());
    });
    yr.addEventListener('change', function () { if (opts.onChange) opts.onChange(get()); });
    function get() { return { make: mk.value, model: md.value, year: yr.value }; }
    function set(v) {
      return ensure().then(function () {
        if (!v || !DB[v.make]) return;
        mk.value = v.make; fill(md, Object.keys(DB[v.make]), 'Model');
        if (!DB[v.make][v.model]) return;
        md.value = v.model; fill(yr, Object.keys(DB[v.make][v.model]), 'Year');
        if (v.year && DB[v.make][v.model][v.year]) yr.value = v.year; else if (Object.keys(DB[v.make][v.model]).length === 1) yr.value = Object.keys(DB[v.make][v.model])[0];
      });
    }
    return { ensure: ensure, get: get, set: set };
  };

  W.$$('[data-vehicle-bar]').forEach(function (bar) {
    var form = bar.querySelector('form');
    var api = W.vehicleSelects(bar);
    var garageEl = bar.querySelector('[data-garage]');
    var tog = bar.querySelector('[data-vb-toggle]');
    if (tog) tog.addEventListener('click', function () {
      var open = form.classList.toggle('is-open');
      tog.setAttribute('aria-expanded', open);
      if (open) { api.ensure(); form.querySelector('[data-vb="make"]').focus(); }
    });
    function showGarage() {
      var g = W.getGarage();
      if (!garageEl) return;
      if (g && g.make) {
        garageEl.hidden = false;
        garageEl.querySelector('[data-garage-name]').textContent = g.make + ' ' + g.model;
        var sub = bar.querySelector('[data-vb-toggle-sub]'); if (sub) sub.textContent = '— ' + g.make + ' ' + g.model;
      } else garageEl.hidden = true;
    }
    showGarage();
    document.addEventListener('wwc:garage', showGarage);
    if (garageEl) {
      garageEl.querySelector('[data-garage-use]').addEventListener('click', function () { var g = W.getGarage(); if (g) window.location.href = W.fitmentHref(g.make, g.model, g.year); });
      garageEl.querySelector('[data-garage-clear]').addEventListener('click', function () { W.store.set(GARAGE_KEY, null); showGarage(); });
    }
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var v = api.get();
      if (!v.make) { api.ensure(); form.querySelector('[data-vb="make"]').focus(); return; }
      if (v.model) W.setGarage(v);
      window.location.href = W.fitmentHref(v.make, v.model, v.year);
    });
  });
})();
